import { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "../components/StaffShell";
import { T, useI18n } from "../lib/i18n";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Scissors, ArrowRight, Check, X, AlertTriangle, Edit3, Printer, PackageOpen, PowerOff, Power } from "lucide-react";
import { VipBadge } from "../components/VipBadge";
import EmbroideryInventoryModal from "../components/EmbroideryInventoryModal";

// Canonical Phase 3B statuses — 3-state workflow (no ACCEPTED)
const COLUMNS = [
  { key: "PENDING", label: "Pending", next: "IN_PROGRESS", nextLabel: "Start" },
  { key: "IN_PROGRESS", label: "In Progress", next: "COMPLETED", nextLabel: "Complete" },
  { key: "COMPLETED", label: "Complete", next: null, nextLabel: null },
];
const NORMALIZE = { READY: "COMPLETED", PICKED_UP: "COMPLETED", ACCEPTED: "IN_PROGRESS" };

function elapsed(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function toCanonical(s) {
  const up = (s || "").toUpperCase();
  return NORMALIZE[up] || up;
}

export default function EmbroideryStaffDash() {
  useI18n();
  const { eventId } = useMotif();
  const [reqs, setReqs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [printOrder, setPrintOrder] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [queueState, setQueueState] = useState({ enabled: true });
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [r, i, q] = await Promise.allSettled([
        api.get("/experiences/requests"),
        api.get("/embroidery/inventory"),
        api.get("/embroidery/queue-state"),
      ]);
      if (r.status === "fulfilled") setReqs(r.value.data);
      if (i.status === "fulfilled") setInventory(i.value.data);
      if (q.status === "fulfilled") setQueueState(q.value.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(iv);
  }, []);

  useEventSocket(eventId, (msg) => {
    if (msg.type === "experience_request_new" || msg.type === "experience_request_update") refresh();
    if (msg.type === "inventory_update" || msg.type === "ws_reconnected") refresh();
    if (msg.type === "embroidery_queue_state") setQueueState(msg.payload);
  });

  const toggleQueue = async () => {
    setToggling(true);
    try {
      const target = !queueState.enabled;
      const { data } = await api.put("/embroidery/queue-state", { enabled: target });
      setQueueState(data);
      toast.success(target ? T("Requests reopened") : T("New requests stopped"));
      setConfirmToggle(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setToggling(false);
    }
  };

  // Optimistic advance so cards move columns without waiting for the server.
  const advance = async (id, targetStatus, nextLabel) => {
    setReqs((prev) => prev.map((r) => (r.id === id ? { ...r, status: targetStatus } : r)));
    try {
      await api.put(`/experiences/requests/${id}/status`, { status: targetStatus });
      toast.success(`${T(nextLabel)} · ${T("saved")}`);
    } catch (e) {
      toast.error(formatErr(e));
      refresh(); // rollback via server
    }
  };

  const doCancel = async () => {
    if (!cancelling) return;
    setReqs((prev) => prev.map((r) => (r.id === cancelling ? { ...r, status: "CANCELLED" } : r)));
    try {
      await api.put(`/experiences/requests/${cancelling}/cancel`, { reason: cancelReason || "cancelled by embroidery staff" });
      toast.success(T("Cancelled · inventory released"));
      setCancelling(null);
      setCancelReason("");
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
      refresh();
    }
  };

  const grouped = useMemo(() => {
    const g = { PENDING: [], IN_PROGRESS: [], COMPLETED: [] };
    reqs.forEach((r) => {
      const s = toCanonical(r.status);
      if (s === "CANCELLED" || s === "PREMADE_DELIVERED") return; // hidden from active board
      (g[s] = g[s] || []).push(r);
    });
    // VIP-first sort within each column, then MOST RECENT first
    Object.keys(g).forEach((k) => {
      g[k].sort((a, b) => {
        const av = a.vip ? 0 : 1, bv = b.vip ? 0 : 1;
        if (av !== bv) return av - bv;
        // Newest first — prefer the most recent stage timestamp available for this column
        const at = a.completed_at || a.started_at || a.requested_at || a.created_at || "";
        const bt = b.completed_at || b.started_at || b.requested_at || b.created_at || "";
        return bt.localeCompare(at);
      });
    });
    return g;
  }, [reqs]);

  const totals = useMemo(() => {
    const pash = inventory.filter((i) => i.item_type === "Pashmina").reduce((s, i) => s + (i.remaining_qty || 0), 0);
    const hank = inventory.filter((i) => i.item_type === "Handkerchief").reduce((s, i) => s + (i.remaining_qty || 0), 0);
    return { pash, hank, combined: pash + hank };
  }, [inventory]);

  const colorsFor = (type) => inventory.filter((i) => i.item_type === type);

  return (
    <StaffShell title={T("Embroidery Studio")} subtitle={T("Live orders and inventory")}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label={T("Pashmina remaining")} value={totals.pash} testid="stat-pashmina" />
        <StatCard label={T("Handkerchief remaining")} value={totals.hank} testid="stat-handkerchief" />
        <StatCard label={T("Combined remaining")} value={totals.combined} testid="stat-combined" />
        <StatCard label={T("Active orders")} value={grouped.PENDING.length + grouped.IN_PROGRESS.length} testid="stat-active" />
      </div>

      {/* Operational controls — queue state + inventory editor */}
      <div className="card-elev p-3 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-luxe text-motif-muted">{T("Embroidery Requests")}:</span>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              background: queueState.enabled ? "#10b98126" : "#ef444426",
              color: queueState.enabled ? "#10b981" : "#ef4444",
            }}
            data-testid="embroidery-queue-badge"
          >
            {queueState.enabled ? T("OPEN") : T("CLOSED")}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => setConfirmToggle(true)}
            className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${queueState.enabled ? "" : "btn-primary"}`}
            style={queueState.enabled ? { background: "#dc2626", color: "#fff" } : undefined}
            data-testid="embroidery-queue-toggle"
          >
            {queueState.enabled ? <><PowerOff className="w-3 h-3" /> {T("END QUEUE")}</> : <><Power className="w-3 h-3" /> {T("Reopen Requests")}</>}
          </button>
          <button
            onClick={() => setShowInventoryModal(true)}
            className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
            data-testid="embroidery-edit-inventory"
          >
            <PackageOpen className="w-3 h-3" /> {T("Edit Inventory")}
          </button>
        </div>
      </div>

      <div className="card-elev p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Scissors className="w-4 h-4 text-primary-motif" />
          <div className="font-display text-lg">{T("Live inventory")}</div>
          <button
            onClick={() => setShowInventoryModal(true)}
            className="btn-ghost text-xs ml-auto flex items-center gap-1"
            data-testid="embroidery-inventory-edit-inline"
            title="Edit any inventory count"
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
        </div>
        {[...new Set(inventory.map((i) => i.item_type))].map((type) => (
          <div key={type} className="mb-3 last:mb-0">
            <div className="text-[10px] tracking-luxe text-motif-muted mb-1.5">{type}</div>
            <div className="flex flex-wrap gap-1.5">
              {colorsFor(type).map((c) => {
                const low = c.remaining_qty > 0 && c.remaining_qty <= 2;
                const out = c.remaining_qty <= 0;
                return (
                  <div
                    key={c.item_type + c.color}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full border"
                    style={{
                      borderColor: out ? "#ef4444" : low ? "#f59e0b" : "var(--motif-border)",
                      background: out ? "#ef44441a" : low ? "#f59e0b1a" : "var(--motif-surface)",
                    }}
                    data-testid={`inv-${type.toLowerCase()}-${c.color.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div
                      className="w-4 h-4 rounded border"
                      style={{ background: c.swatch_hex || "#CCC", borderColor: "var(--motif-border)" }}
                    />
                    <span className="text-[11px] text-motif">{c.color}</span>
                    <span className="text-[10px] font-bold" style={{ color: out ? "#ef4444" : low ? "#f59e0b" : "var(--motif-primary)" }}>
                      {c.remaining_qty}
                    </span>
                    {(out || low) && <AlertTriangle className="w-3 h-3" style={{ color: out ? "#ef4444" : "#f59e0b" }} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {COLUMNS.map((col) => (
          <Column
            key={col.key}
            col={col}
            items={grouped[col.key] || []}
            onAdvance={advance}
            onCancel={(id) => setCancelling(id)}
            onOpenPrint={(r) => setPrintOrder(r)}
            inventory={inventory}
          />
        ))}
      </div>

      {printOrder && <PrintTicketModal order={printOrder} onClose={() => setPrintOrder(null)} />}

      {showInventoryModal && (
        <EmbroideryInventoryModal onClose={() => setShowInventoryModal(false)} onChanged={refresh} />
      )}

      {confirmToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="embroidery-queue-confirm">
          <div className="card-elev p-5 w-full max-w-md">
            <div className="font-display text-xl text-motif mb-2">
              {queueState.enabled ? T("End the embroidery queue for the night?") : T("Reopen embroidery requests?")}
            </div>
            <p className="text-sm text-motif-muted mb-4">
              {queueState.enabled
                ? T("This will stop guests from submitting new embroidery requests. Existing orders will remain available for processing.")
                : T("Guests will be able to submit new embroidery requests again. Only colors with remaining stock will be shown.")}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmToggle(false)}
                className="btn-ghost text-xs flex-1"
                data-testid="embroidery-queue-confirm-cancel"
              >
                Keep {queueState.enabled ? "open" : "closed"}
              </button>
              <button
                onClick={toggleQueue}
                disabled={toggling}
                className={`text-xs flex-1 px-3 py-2 rounded-lg font-medium transition ${queueState.enabled ? "" : "btn-primary"}`}
                style={queueState.enabled ? { background: "#dc2626", color: "#fff" } : undefined}
                data-testid="embroidery-queue-confirm-yes"
              >
                {toggling ? T("Saving…") : queueState.enabled ? T("Yes, END QUEUE") : T("Yes, reopen")}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="embroidery-cancel-dialog">
          <div className="card-elev p-5 w-full max-w-sm">
            <div className="font-display text-xl text-motif mb-2">{T("Cancel order")}</div>
            <p className="text-sm text-motif-muted mb-3">{T("Inventory will be released back to stock.")}</p>
            <input
              className="input-motif"
              placeholder={T("Reason (optional)")}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              autoFocus
              data-testid="embroidery-cancel-reason"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setCancelling(null); setCancelReason(""); }} className="btn-ghost text-xs flex-1">{T("Keep")}</button>
              <button onClick={doCancel} className="btn-primary text-xs flex-1" data-testid="embroidery-cancel-confirm">
                Cancel order
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffShell>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="card-elev p-4" data-testid={testid}>
      <div className="text-[10px] tracking-luxe text-motif-muted">{label}</div>
      <div className="font-display text-3xl text-primary-motif">{value}</div>
    </div>
  );
}

function Column({ col, items, onAdvance, onCancel, onOpenPrint, inventory }) {
  return (
    <div className="card-elev p-3" data-testid={`embroidery-col-${col.key.toLowerCase().replace(/_/g, "-")}`}>
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-motif">
        <div className="font-display text-sm text-motif">{T(col.label)}</div>
        <div className="text-[10px] tracking-luxe text-primary-motif">{items.length}</div>
      </div>
      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {items.map((r) => (
          <OrderCard key={r.id} r={r} col={col} onAdvance={onAdvance} onCancel={onCancel} onOpenPrint={onOpenPrint} inventory={inventory} />
        ))}
        {items.length === 0 && <div className="text-[11px] text-motif-muted italic text-center py-2">{T("Empty")}</div>}
      </div>
    </div>
  );
}

function OrderCard({ r, col, onAdvance, onCancel, onOpenPrint, inventory }) {
  const color = (r.options || {}).color;
  const thread = (r.options || {}).thread_color;
  const invRow = inventory.find((i) => i.item_type === r.item && i.color === color);
  const tableLabel = r.table_name || (r.table_number ? `Table ${r.table_number}` : "");
  const submitted = r.requested_at || r.created_at;
  const source = r.source === "workbook_pre_import" ? "Pre-made" : "Venuefy";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenPrint(r)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenPrint(r); } }}
      className="p-2.5 rounded-xl bg-accent-motif space-y-2 cursor-pointer hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-[color:var(--motif-primary)]"
      data-testid={`embroidery-order-${r.id}`}
      aria-label={`Open printable ticket for ${r.guest_name}`}
    >
      <div className="flex items-start gap-2">
        {color && (
          <div
            className="w-9 h-9 rounded-md border flex-shrink-0"
            style={{ background: invRow?.swatch_hex || "#CCCCCC", borderColor: "var(--motif-border)" }}
            title={color}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm text-motif leading-tight flex items-center gap-1.5">
            <VipBadge show={!!r.vip} />
            <span>{r.item} · <span className="tracking-widest">&ldquo;{r.text}&rdquo;</span></span>
          </div>
          <div className="text-[10px] text-motif-muted truncate">
            <span data-testid={`order-guest-${r.id}`}>{r.guest_name}</span>
            {tableLabel && <> · {tableLabel}</>}
            {r.seat ? ` · Seat ${r.seat}` : ""}
          </div>
          <div className="text-[10px] text-motif-muted">
            {color}{thread ? ` · thread: ${thread}` : ""}
          </div>
          <div className="text-[9px] tracking-luxe text-motif-muted mt-1 flex items-center gap-1.5">
            <span>{source}</span>
            {submitted && (
              <>
                <span>·</span>
                <span data-testid={`order-elapsed-${r.id}`}>{elapsed(submitted)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {col.next && (
        <div className="flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onAdvance(r.id, col.next, col.nextLabel); }}
            className="btn-primary text-[11px] px-2 py-1.5 flex items-center gap-1 flex-1 justify-center"
            data-testid={`embroidery-advance-${r.id}`}
          >
            {col.nextLabel === "Complete" ? <Check className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
            {T(col.nextLabel)}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCancel(r.id); }}
            className="btn-ghost text-[11px] px-2 py-1.5"
            data-testid={`embroidery-cancel-${r.id}`}
            aria-label="Cancel order"
            title="Cancel order"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {!col.next && (
        <div className="text-[10px] text-primary-motif font-medium tracking-luxe text-center pt-1">
          Ready for pickup ✓
        </div>
      )}
    </div>
  );
}

function PrintTicketModal({ order, onClose }) {
  const tableLabel = order.table_name || (order.table_number ? `Table ${order.table_number}` : "");
  const [printed, setPrinted] = useState(false);

  const doPrint = () => {
    setPrinted(true);
    // Browser handles print; may or may not physically succeed
    setTimeout(() => window.print(), 50);
  };

  // Close on Escape (does NOT refresh dashboard state)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 print:bg-transparent print:p-0 print:relative print:inset-auto"
      onClick={onClose}
      data-testid="print-ticket-modal"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md print:shadow-none print:rounded-none print:max-w-none print:w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close (hidden on print) */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center print:hidden"
          aria-label="Close"
          data-testid="print-ticket-close"
        >
          <X className="w-4 h-4 text-gray-700" strokeWidth={2} />
        </button>

        {/* Printable ticket area — 4x6 Rollo label, one order per label */}
        <div id="rollo-ticket" className="print-ticket p-8 flex flex-col items-center justify-center text-black">
          {/* Guest name */}
          <div className="text-xl font-medium text-gray-900 text-center" data-testid="ticket-guest-name">
            {order.guest_name}
          </div>
          {/* Table + Seat */}
          {tableLabel && (
            <div className="text-base text-gray-700 mt-1 text-center" data-testid="ticket-table-seat">
              {tableLabel}{order.seat ? ` · Seat ${order.seat}` : ""}
            </div>
          )}
          {/* Item type + Fabric color (compact) */}
          <div className="mt-4 text-sm text-gray-700 text-center leading-tight">
            <div data-testid="ticket-item">Item: <span className="font-semibold">{order.item}</span></div>
            {(order.options || {}).color && (
              <div data-testid="ticket-fabric-color">
                Fabric Color: <span className="font-semibold">{order.options.color}</span>
              </div>
            )}
          </div>
          {/* Thread color — same size as Item & Fabric Color */}
          {(order.options || {}).thread_color && (
            <div className="text-sm text-gray-700 text-center" data-testid="ticket-thread-color">
              Thread: <span className="font-semibold">{order.options.thread_color}</span>
            </div>
          )}
          {/* Embroidery text — largest, preserves EXACT case */}
          <div
            className="mt-4 tracking-widest text-center"
            style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', serif", fontSize: "8rem", lineHeight: 1, fontWeight: 600 }}
            data-testid="ticket-embroidery-text"
          >
            &ldquo;{order.text}&rdquo;
          </div>
        </div>

        {/* Actions (hidden on print) */}
        <div className="p-4 border-t border-gray-100 flex gap-2 print:hidden">
          <button
            onClick={doPrint}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            data-testid="print-ticket-btn"
          >
            <Printer className="w-4 h-4" strokeWidth={1.5} />
            Print
          </button>
        </div>
        {printed && (
          <div className="px-4 pb-3 text-[10px] text-gray-500 text-center print:hidden">
            Print dialog opened — physical print not verified.
          </div>
        )}
      </div>
    </div>
  );
}
